// Shared helper to get/update tokens from storage
// This is used by all direct API calls to authenticate with Supabase

/// <reference lib="dom" />

export interface StoredSession {
  access_token: string
  refresh_token: string
  expires_at: number
}

function getSessionFromStorage(): StoredSession | null {
  if (typeof window === 'undefined') return null

  // Try sessionStorage first (Zustand authStore)
  const authStorage = window.sessionStorage.getItem('auth-storage')
  if (authStorage) {
    try {
      const authData = JSON.parse(authStorage)
      const session = authData?.state?.session
      if (session?.access_token) return session
    } catch { /* fall through */ }
  }

  // Fallback to localStorage
  const sessionStr = window.localStorage.getItem('supabase.auth.token')
  if (!sessionStr) return null
  try {
    const data = JSON.parse(sessionStr)
    return data?.currentSession || null
  } catch {
    return null
  }
}

export function getAccessToken(): string | null {
  return getSessionFromStorage()?.access_token ?? null
}

export function getRefreshToken(): string | null {
  return getSessionFromStorage()?.refresh_token ?? null
}

export function getSessionExpiresAt(): number | null {
  return getSessionFromStorage()?.expires_at ?? null
}

export function isSessionExpired(): boolean {
  const expiresAt = getSessionExpiresAt()
  if (!expiresAt) return true
  // Consider expired 60 seconds early to avoid edge cases
  return Date.now() / 1000 > expiresAt - 60
}

/**
 * Update the stored session after a token refresh.
 * Writes to both sessionStorage (Zustand) and localStorage.
 */
export function updateStoredSession(session: StoredSession): void {
  if (typeof window === 'undefined') return

  // Update sessionStorage (Zustand auth-storage)
  const authStorage = window.sessionStorage.getItem('auth-storage')
  if (authStorage) {
    try {
      const authData = JSON.parse(authStorage)
      if (authData?.state) {
        authData.state.session = session
        window.sessionStorage.setItem('auth-storage', JSON.stringify(authData))
      }
    } catch { /* ignore */ }
  }

  // Update localStorage fallback
  const sessionStr = window.localStorage.getItem('supabase.auth.token')
  if (sessionStr) {
    try {
      const data = JSON.parse(sessionStr)
      if (data?.currentSession) {
        data.currentSession.access_token = session.access_token
        data.currentSession.refresh_token = session.refresh_token
        data.currentSession.expires_at = session.expires_at
        window.localStorage.setItem('supabase.auth.token', JSON.stringify(data))
      }
    } catch { /* ignore */ }
  }
}
